import type { PromotionCodeDiscount } from "@biteasy/contracts";
import type { StripeElements } from "@stripe/stripe-js";
import { useMutation } from "@tanstack/react-query";
import { Tag, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/catalyst/Button";
import { Input } from "@/components/catalyst/Input";
import { orpc } from "@/orpc/client";

/** Format a discount for display (e.g., "20% off for 3 months") */
function formatDiscount(discount: PromotionCodeDiscount): string {
	const parts: string[] = [];

	// Monetary/percentage discount
	if (discount.percentOff) {
		let discountText = `${discount.percentOff}% off`;
		if (discount.duration === "repeating" && discount.durationInMonths) {
			discountText += ` for ${discount.durationInMonths} month${discount.durationInMonths > 1 ? "s" : ""}`;
		} else if (discount.duration === "once") {
			discountText += " (first payment)";
		}
		parts.push(discountText);
	} else if (discount.amountOff && discount.currency) {
		const amount = discount.amountOff / 100;
		let discountText = `$${amount} off`;
		if (discount.duration === "repeating" && discount.durationInMonths) {
			discountText += ` for ${discount.durationInMonths} month${discount.durationInMonths > 1 ? "s" : ""}`;
		} else if (discount.duration === "once") {
			discountText += " (first payment)";
		}
		parts.push(discountText);
	}

	// Trial days
	if (discount.trialDays) {
		parts.push(`${discount.trialDays}-day free trial`);
	}

	return parts.join(" + ");
}

/**
 * Promo code input that applies/removes discounts on the existing subscription
 * in-place via API mutation, then calls elements.fetchUpdates() so the
 * PaymentElement reflects the updated amount without remounting.
 */
export function PromoCodeSection({
	appliedDiscount,
	elements,
	onApplied,
	onRecreated,
}: {
	appliedDiscount: PromotionCodeDiscount | null;
	elements: StripeElements | null;
	onApplied: (discount: PromotionCodeDiscount | null) => void;
	/** Called when the subscription was recreated (e.g., paid → trial) with a new clientSecret */
	onRecreated?: (clientSecret: string, isSetupIntent: boolean) => void;
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [code, setCode] = useState("");

	const {
		mutate: applyCode,
		isPending,
		error,
	} = useMutation({
		...orpc.billing.applyPromotionCode.mutationOptions(),
		onSuccess: async (data) => {
			onApplied(data.discount);
			setCode("");

			if (data.clientSecret && data.isSetupIntent != null) {
				// Subscription was recreated (e.g., paid → trial) — remount Elements
				onRecreated?.(data.clientSecret, data.isSetupIntent);
			} else {
				// In-place update — refresh the PaymentElement
				await elements?.fetchUpdates();
			}
		},
	});

	const handleApply = () => {
		const trimmed = code.trim();
		if (trimmed) {
			applyCode({ code: trimmed });
		}
	};

	const handleRemove = () => {
		applyCode({ code: null });
	};

	// Show applied discount badge
	if (appliedDiscount) {
		return (
			<div className="flex items-center justify-center gap-2">
				<span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">
					<Tag className="size-3" />
					{formatDiscount(appliedDiscount)}
				</span>
				<button
					type="button"
					onClick={handleRemove}
					disabled={isPending}
					className="text-base-content/40 hover:text-base-content/60 transition-colors disabled:opacity-50"
					aria-label="Remove promotion code"
				>
					<X className="size-4" />
				</button>
			</div>
		);
	}

	// Show toggle link or input form
	if (!isOpen) {
		return (
			<div className="text-center">
				<button
					type="button"
					onClick={() => setIsOpen(true)}
					className="text-xs text-base-content/50 hover:text-base-content/70 underline underline-offset-2 transition-colors"
				>
					Have a promo code?
				</button>
			</div>
		);
	}

	return (
		<div>
			<div className="flex gap-2">
				<Input
					type="text"
					value={code}
					onChange={(e) => setCode(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							handleApply();
						}
					}}
					placeholder="Enter promo code"
					className="flex-1"
					autoFocus
				/>
				<Button
					type="button"
					outline
					disabled={isPending || !code.trim()}
					onClick={handleApply}
				>
					{isPending ? "..." : "Apply"}
				</Button>
			</div>
			{error && <p className="mt-1.5 text-xs text-error">{error.message}</p>}
		</div>
	);
}
